import type { Dispatch, MutableRefObject, ReactNode } from 'react'
import { createContext, useContext } from 'react'
import type SvgIcon from '../SvgIcon/SvgIcon'
import { Directions } from '../utilities/directions'
import type { Actions } from './Actions'
import { ActionTypes } from './Actions'

export interface GroupedTableColumns {
  selected: TableColumn[]
  unselected: TableColumn[]
}

export type ColumnWidth = number

export type Pagination = 'simple' | 'lengthAware'

export type Limit = 25 | 50 | 100

export type FilterOptions = {
  columnKey: string
  value: string
  checked: boolean
}

export type SortOptions = {
  direction: Directions.Ascending | Directions.Descending
  key: keyof TableRow
}

export type ClickRowActionEventHandler = (row: TableRow) => void

export type FilterEventHandler = ({
  columnKey,
  value,
  checked,
}: FilterOptions) => void

export type SelectEventHandler = (rows: TableRow[]) => void

export type SortEventHandler = ({ direction, key }: SortOptions) => void

export interface TableColumn {
  /**
   * Array of filter options for this column. Each filter option contains a label for display, a value for identification, and a checked state
   */
  filter?: { label: string; value: string; checked: boolean }[]
  /**
   * Unique identifier for the column. Used to match column definitions with row data
   */
  key: string
  /**
   * Whether the column is currently visible in the table
   */
  selected: boolean
  /**
   * Current sort direction applied to this column. Only one column can have a sort direction at a time
   */
  sort?: Directions.Ascending | Directions.Descending
  /**
   * Whether the column can be sorted by the user
   * @default false
   */
  sortable?: boolean
  /**
   * The content to display in the column header
   */
  value: ReactNode
  /**
   * The width configuration for the column supporting a percentage value.
   * If not provided, the column will size automatically based on its content.
   */
  width?: ColumnWidth
}

type TableReducers = {
  [ActionType in ActionTypes]: (
    state: TableState,
    actions: Actions,
  ) => TableState
}

export interface TableRow {
  // TODO: Figure out if there is a way to make the TableRow `key` correspond to TableColumn `key`
  [key: string]: string | ReactNode
  id: string
  selected?: boolean
}

export interface TableState {
  columns: GroupedTableColumns
  dense: boolean
  isScrolling?: boolean
  limit: Limit
  loading: boolean
  onFilterChange?: FilterEventHandler
  onSelect?: SelectEventHandler
  onSort?: SortEventHandler
  page: number
  pagination: Pagination
  rowActions?: {
    enabled?: (row: TableRow) => boolean
    label: ReactNode
    handler: ClickRowActionEventHandler
    startAdornment?: typeof SvgIcon
  }[]
  rows: TableRow[]
  scrollable: boolean
  selectionsRef?: MutableRefObject<string[]>
  selectable: boolean
  allSelected: boolean
  staticColumns: boolean
  total?: number
}

type UseTableValues = [TableState, Dispatch<Actions>]

/**
 * Partition columns into selected (visible) and unselected (hidden) groups
 *
 * @param columns
 * @returns GroupedTableColumns
 */
export const partition = (columns: TableColumn[]): GroupedTableColumns =>
  columns.reduce(
    (previous, current) => {
      if (current.selected) {
        previous.selected.push(current)
      } else {
        previous.unselected.push(current)
      }

      return previous
    },
    { selected: [], unselected: [] } as GroupedTableColumns,
  )

/**
 * Populate columns from localStorage if available
 *
 * @param defaultColumns
 * @param initialRender
 * @param storageKey
 * @returns TableColumn[]
 */
export const populate = (
  defaultColumns: TableColumn[],
  initialRender: boolean,
  storageKey?: string,
): TableColumn[] => {
  if (storageKey && initialRender) {
    const serializedColumns = window.localStorage.getItem(storageKey)

    if (serializedColumns) {
      const storedColumns = JSON.parse(serializedColumns) as TableColumn[]
      const newColumns = defaultColumns.filter(
        (defaultColumn) =>
          !storedColumns.some(
            (storedColumn) => defaultColumn.key === storedColumn.key,
          ),
      )

      return storedColumns
        .filter((storedColumn) =>
          defaultColumns.find(
            (defaultColumn) => defaultColumn.key === storedColumn.key,
          ),
        )
        .map((storedColumn) => {
          const defaultColumn = defaultColumns.find(
            (defaultColumn) => defaultColumn.key === storedColumn.key,
          )
          const column: TableColumn = {
            ...storedColumn,
            value: defaultColumn?.value || storedColumn.value,
          }

          if (defaultColumn?.width !== undefined) {
            column.width = defaultColumn.width
          } else {
            // Remove width if default doesn't specify one, to reflect current code intention
            delete column.width
          }

          if (defaultColumn?.filter) {
            column.filter = [...defaultColumn.filter]
          }

          return column
        })
        .concat(newColumns)
    }
  }

  return defaultColumns
}

const reducers: TableReducers = {
  [ActionTypes.ChangeLimit]: (
    state,
    action: Extract<Actions, { type: ActionTypes.ChangeLimit }>,
  ) => {
    // 1-based index of the first visible row on the current page
    const anchoredFirstRowIndex = (state.page - 1) * state.limit + 1
    const newLimit = action.limit
    const newPage = Math.ceil(anchoredFirstRowIndex / newLimit)

    return {
      ...state,
      limit: newLimit,
      page: newPage,
    }
  },
  [ActionTypes.FilterChange]: (
    state,
    action: Extract<Actions, { type: ActionTypes.FilterChange }>,
  ) => {
    const columns = [...state.columns.selected, ...state.columns.unselected]

    return {
      ...state,
      columns: partition(
        columns.map((column) => {
          if (column.key === action.key) {
            column.filter?.map((filter) => {
              if (filter.value === action.value) {
                filter.checked = action.checked

                state.onFilterChange?.({
                  columnKey: action.key,
                  value: action.value,
                  checked: action.checked,
                })
              }

              return filter
            })
          }

          return column
        }),
      ),
    }
  },
  [ActionTypes.FirstPage]: (state) => ({ ...state, page: 1 }),
  [ActionTypes.MoveColumn]: (
    state,
    action: Extract<Actions, { type: ActionTypes.MoveColumn }>,
  ) => {
    let columns = state.columns.selected

    if (state.selectable) {
      columns = columns.slice(1)
    }

    const oldIndex = columns.findIndex((column) => column.key === action.key)
    const newIndex =
      action.direction === Directions.Left ? oldIndex - 1 : oldIndex + 1

    columns.splice(newIndex, 0, columns.splice(oldIndex, 1)[0])

    return {
      ...state,
      columns: partition([
        ...(state.selectable ? [state.columns.selected[0]] : []),
        ...columns,
        ...state.columns.unselected,
      ]),
    }
  },
  [ActionTypes.NextPage]: (state) => ({ ...state, page: state.page + 1 }),
  [ActionTypes.PreviousPage]: (state) => ({ ...state, page: state.page - 1 }),
  [ActionTypes.SortColumn]: (
    state,
    action: Extract<Actions, { type: ActionTypes.SortColumn }>,
  ) => {
    const columns = [...state.columns.selected, ...state.columns.unselected]

    return {
      ...state,
      columns: partition(
        columns.map((column) => {
          if (column.key === action.key) {
            column.sort = action.direction

            state.onSort?.({
              direction: column.sort,
              key: action.key,
            })
          } else {
            column.sort = undefined
          }

          return column
        }),
      ),
      page: 1,
    }
  },
  [ActionTypes.ToggleColumn]: (
    state,
    action: Extract<Actions, { type: ActionTypes.ToggleColumn }>,
  ) => {
    const columns = [...state.columns.selected, ...state.columns.unselected]

    state.columns.selected.forEach((column, index, columns) => {
      if (column.key === action.key) {
        const focusable = columns[index + 1] || columns[index - 1]
        const element = document.querySelector(
          `th[data-key="${focusable.key}"]`,
        ) as HTMLTableHeaderCellElement

        element?.focus()
        element?.setAttribute('tabIndex', '0')
      }
    })

    return {
      ...state,
      columns: partition(
        columns.map((column) => {
          if (column.key === action.key) {
            column.selected = !column.selected
          }

          return column
        }),
      ),
    }
  },
  [ActionTypes.ToggleRow]: (
    state,
    action: Extract<Actions, { type: ActionTypes.ToggleRow }>,
  ) => {
    const rows = state.rows.map((row) => {
      if (row.id === action.key) {
        row.selected = !row.selected

        if (action.selectionsRef === undefined) {
          return row
        }

        if (row.selected) {
          action.selectionsRef.current.push(row.id)
        } else {
          const deselectedRow = action.selectionsRef.current.indexOf(row.id)
          action.selectionsRef.current.splice(deselectedRow, 1)
        }
      }

      return row
    })

    const selectedRows = state.rows.filter((row) => row.selected)

    if (state.onSelect) {
      state.onSelect(selectedRows)
    }

    return {
      ...state,
      allSelected: selectedRows.length == state.limit,
      rows,
    }
  },
  [ActionTypes.ToggleRows]: (
    state,
    action: Extract<Actions, { type: ActionTypes.ToggleRows }>,
  ) => {
    const selected = action.status === 'checked'
    const rows = state.rows.map((row) => {
      row.selected = selected

      if (action.selectionsRef === undefined) {
        return row
      }

      if (row.selected && action.selectionsRef.current.indexOf(row.id) === -1) {
        action.selectionsRef.current.push(row.id)
      }

      if (!row.selected) {
        const deselectedRow = action.selectionsRef.current.indexOf(row.id)
        action.selectionsRef.current.splice(deselectedRow, 1)
      }

      return row
    })

    if (state.onSelect) {
      state.onSelect(state.rows.filter((row) => row.selected))
    }

    return {
      ...state,
      allSelected: selected,
      rows,
    }
  },
  [ActionTypes.UpdateRows]: (
    state,
    action: Extract<Actions, { type: ActionTypes.UpdateRows }>,
  ) => {
    const selectedRows = state.rows.filter((row) => row.selected)

    if (selectedRows.length > 0) {
      for (const selectedRow of selectedRows) {
        const index = action.rows.findIndex((row) => row.id === selectedRow.id)

        if (index > -1) {
          action.rows[index].selected = true
        }
      }
    }

    if (action.selectionsRef) {
      action.selectionsRef.current = action.selectionsRef.current.filter(
        (selectedRow) =>
          action.rows.find((row) => row.id === selectedRow) !== undefined,
      )
    }

    let page = state.page

    if ((action.total as number) < (state.page - 1) * state.limit + 1) {
      page = Math.ceil((action.total as number) / state.limit)
    } else if (action.rows.length === 0 && state.page > 1) {
      page = state.page - 1
    }

    return {
      ...state,
      page: page > 0 ? page : 1,
      rows: action.rows,
      total: action.total,
    }
  },
  [ActionTypes.UpdateRowActions]: (
    state,
    action: Extract<Actions, { type: ActionTypes.UpdateRowActions }>,
  ) => {
    return {
      ...state,
      rowActions: action.rowActions,
    }
  },
  [ActionTypes.UpdateLoading]: (
    state,
    action: Extract<Actions, { type: ActionTypes.UpdateLoading }>,
  ) => {
    return {
      ...state,
      loading: action.loading,
    }
  },
  [ActionTypes.UpdateColumns]: (
    state,
    action: Extract<Actions, { type: ActionTypes.UpdateColumns }>,
  ) => {
    return {
      ...state,
      columns: action.columns,
    }
  },
  [ActionTypes.UpdateScrolling]: function (
    state,
    actions: Extract<Actions, { type: ActionTypes.UpdateScrolling }>,
  ): TableState {
    return {
      ...state,
      isScrolling: actions.isScrolling,
    }
  },
  [ActionTypes.UpdateScrollable]: function (
    state,
    actions: Extract<Actions, { type: ActionTypes.UpdateScrollable }>,
  ): TableState {
    return {
      ...state,
      scrollable: actions.scrollable,
    }
  },
  [ActionTypes.UpdateDense]: function (
    state,
    actions: Extract<Actions, { type: ActionTypes.UpdateDense }>,
  ): TableState {
    return {
      ...state,
      dense: actions.dense,
    }
  },
  [ActionTypes.UpdateSelectable]: function (
    state,
    actions: Extract<Actions, { type: ActionTypes.UpdateSelectable }>,
  ): TableState {
    const columns = {
      selected: [...state.columns.selected],
      unselected: [...state.columns.unselected],
    }

    // If selectable is being set to false, remove the checkbox column
    if (!actions.selectable) {
      const checkboxIndex = columns.selected.findIndex(
        (column) => column.key === 'checkbox',
      )

      if (checkboxIndex !== -1) {
        columns.selected = [
          ...columns.selected.slice(0, checkboxIndex),
          ...columns.selected.slice(checkboxIndex + 1),
        ]
      }
    }
    // If selectable is being set to true, Head.tsx will handle adding the checkbox column

    return {
      ...state,
      columns,
      selectable: actions.selectable,
    }
  },
}

export const runReducer = (state: TableState, action: Actions): TableState =>
  reducers[action.type](state, action)

export const TableContext = createContext<UseTableValues | null>(null)

export const useTable = (): UseTableValues =>
  useContext(TableContext) as UseTableValues
